import { useState, useEffect, useMemo, useCallback } from "react";
import { axiosInstance } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import {
  Search, Users, Network, ChevronsRight, TrendingUp, FileDown, X,
} from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return "-"; }
}
function formatCurrency(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatKwh(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + " kWh";
}
function formatMonth(monthStr) {
  if (!monthStr) return "";
  const [year, month] = monthStr.split("-");
  const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${months[parseInt(month, 10) - 1] || month}/${year}`;
}

const SITUATION_MAP = {
  active: { label: "Ativo", emoji: "🟢", cls: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", emoji: "🔴", cls: "bg-red-100 text-red-700" },
  sent_for_signature: { label: "Enviado p/ Assinatura", emoji: "🔵", cls: "bg-blue-100 text-blue-700" },
  suspended: { label: "Suspenso", emoji: "🟠", cls: "bg-orange-100 text-orange-700" },
  excluded: { label: "Excluído", emoji: "⚫", cls: "bg-gray-100 text-gray-700" },
  missing_login: { label: "Falta de Login", emoji: "🟣", cls: "bg-purple-100 text-purple-700" },
  modality_change: { label: "Troca de Modalidade", emoji: "🔄", cls: "bg-cyan-100 text-cyan-700" },
  awaiting_ownership_transfer: { label: "Aguard. Troca Tit.", emoji: "🟦", cls: "bg-indigo-100 text-indigo-700" },
  ownership_transfer_requested: { label: "Troca Solicitada", emoji: "🟪", cls: "bg-fuchsia-100 text-fuchsia-700" },
  pending: { label: "Pendente", emoji: "🟡", cls: "bg-yellow-100 text-yellow-700" },
};
const situationInfo = (s) => SITUATION_MAP[s] || SITUATION_MAP.pending;

/**
 * Reusable partner consult panel.
 *
 * Props:
 *  - partnerId (required): the partner id to load
 *  - showFilters: 'date' (default) shows only date range; 'none' hides filters entirely
 *  - showHierarchy: boolean (default true) — shows the hierarchy card and kWh chart
 *  - title: optional string to override the section title
 */
export default function PartnerCooperativesPanel({
  partnerId,
  showFilters = "date",
  showHierarchy = true,
  title,
}) {
  const [sigStart, setSigStart] = useState("");
  const [sigEnd, setSigEnd] = useState("");
  const [subtree, setSubtree] = useState(null);
  const [loadingSubtree, setLoadingSubtree] = useState(false);
  const [cooperativesData, setCooperativesData] = useState(null);
  const [loadingCoops, setLoadingCoops] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const buildDateParams = useCallback(() => {
    const params = {};
    if (sigStart) params.signature_start = sigStart;
    if (sigEnd) params.signature_end = sigEnd;
    return params;
  }, [sigStart, sigEnd]);

  const runQuery = useCallback(async (pid, params) => {
    if (!pid) return;
    setLoadingSubtree(true);
    setLoadingCoops(true);
    const qs = new URLSearchParams(params).toString();
    const suffix = qs ? `?${qs}` : "";
    try {
      const requests = [
        axiosInstance.get(`/partners/${encodeURIComponent(pid)}/cooperatives${suffix}`),
      ];
      if (showHierarchy) {
        requests.push(axiosInstance.get(`/partners/${encodeURIComponent(pid)}/subtree${suffix}`));
      }
      const results = await Promise.all(requests);
      setCooperativesData(results[0].data);
      if (showHierarchy) setSubtree(results[1].data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar dados do parceiro");
    } finally {
      setLoadingSubtree(false);
      setLoadingCoops(false);
    }
  }, [showHierarchy]);

  useEffect(() => {
    if (partnerId) {
      runQuery(partnerId, buildDateParams());
    }
  }, [partnerId, runQuery, buildDateParams]);

  const handleApplyFilter = () => runQuery(partnerId, buildDateParams());
  const handleClearFilters = () => {
    setSigStart("");
    setSigEnd("");
    runQuery(partnerId, {});
  };

  const handleExportLinkedCsv = () => {
    if (!ensureCsvPassword()) return;
    if (!cooperativesData?.cooperatives?.length) {
      toast.info("Nenhum cooperado para exportar.");
      return;
    }
    const rows = cooperativesData.cooperatives.map(c => ({
      "Nome": c.name || "",
      "Distribuidora": c.distributor || "",
      "kWh Contratado": (c.contracted_kwh || 0).toString().replace('.', ','),
      "Data Assinatura": c.signature_date ? new Date(c.signature_date).toLocaleDateString("pt-BR") : "",
      "Status": situationInfo(c.situation).label,
      "Parceiro Direto": c.partner_name || "",
    }));
    const headers = Object.keys(rows[0]);
    const csvContent = "\ufeff" + headers.join(";") + "\n" +
      rows.map(r => headers.map(h => `"${(r[h] || "").toString().replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `cooperados_${(cooperativesData.root_name || "parceiro").replace(/\s+/g, "_")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totals = useMemo(() => {
    if (!subtree?.partners) return null;
    return subtree.partners.reduce((acc, p) => {
      acc.cooperatives += p.cooperatives_count || 0;
      acc.kwh += p.total_kwh || 0;
      acc.received += p.commission_received || 0;
      acc.to_receive += p.commission_to_receive || 0;
      return acc;
    }, { cooperatives: 0, kwh: 0, received: 0, to_receive: 0 });
  }, [subtree]);

  const statusBreakdown = useMemo(() => {
    const list = cooperativesData?.cooperatives || [];
    const map = {};
    for (const c of list) {
      const key = c.situation || "pending";
      if (!map[key]) map[key] = { count: 0, kwh: 0 };
      map[key].count += 1;
      map[key].kwh += Number(c.contracted_kwh || 0);
    }
    const order = [
      "active", "pending", "sent_for_signature", "modality_change", "awaiting_ownership_transfer", "ownership_transfer_requested",
      "suspended", "missing_login", "cancelled", "excluded",
    ];
    return order
      .filter((k) => map[k])
      .map((k) => ({ situation: k, ...map[k], info: SITUATION_MAP[k] || SITUATION_MAP.pending }));
  }, [cooperativesData]);

  // Reset page whenever the underlying dataset changes (new fetch)
  useEffect(() => {
    setCurrentPage(1);
  }, [cooperativesData]);

  const totalPages = Math.max(
    1,
    Math.ceil((cooperativesData?.cooperatives?.length || 0) / itemsPerPage)
  );

  const paginatedCooperatives = useMemo(() => {
    const list = cooperativesData?.cooperatives || [];
    const start = (currentPage - 1) * itemsPerPage;
    return list.slice(start, start + itemsPerPage);
  }, [cooperativesData, currentPage, itemsPerPage]);

  const hasDateFilter = sigStart || sigEnd;
  const isLoading = loadingSubtree || loadingCoops;

  return (
    <div className="space-y-6" data-testid="partner-cooperatives-panel">
      {showFilters === "date" && (
        <Card className="border-0 shadow-lg" data-testid="panel-filters-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              Filtrar por Data de Assinatura
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <Label htmlFor="panel-sig-start" className="mb-1 block">Início</Label>
                <Input
                  id="panel-sig-start"
                  type="date"
                  data-testid="panel-signature-start-input"
                  value={sigStart}
                  onChange={(e) => setSigStart(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="panel-sig-end" className="mb-1 block">Fim</Label>
                <Input
                  id="panel-sig-end"
                  type="date"
                  data-testid="panel-signature-end-input"
                  value={sigEnd}
                  onChange={(e) => setSigEnd(e.target.value)}
                />
              </div>
              <div>
                <Button
                  data-testid="panel-apply-filter-btn"
                  onClick={handleApplyFilter}
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Aplicar filtro
                </Button>
              </div>
              {hasDateFilter && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  {sigStart && <Badge variant="outline">Início: {new Date(sigStart).toLocaleDateString("pt-BR")}</Badge>}
                  {sigEnd && <Badge variant="outline">Fim: {new Date(sigEnd).toLocaleDateString("pt-BR")}</Badge>}
                  <button
                    type="button"
                    data-testid="panel-clear-date-filter"
                    onClick={handleClearFilters}
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> limpar
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="text-center py-8 text-gray-500" data-testid="panel-loading">
          Carregando dados...
        </div>
      )}

      {!loadingCoops && cooperativesData && (
        <Card className="border-0 shadow-lg" data-testid="panel-linked-cooperatives-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {title || (<>Cooperados vinculados a <span className="text-blue-700">{cooperativesData.root_name}</span></>)}
            </CardTitle>
            <Button
              data-testid="panel-export-linked-csv-btn"
              variant="outline"
              onClick={handleExportLinkedCsv}
              disabled={!cooperativesData?.cooperatives?.length}
            >
              <FileDown className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="panel-card-total">
                <p className="text-xs text-gray-600">Total contratados</p>
                <p className="text-2xl font-bold text-blue-700" data-testid="panel-linked-count">{cooperativesData.count}</p>
                <p className="text-xs text-gray-500 mt-1" data-testid="panel-linked-total-kwh">{formatKwh(cooperativesData.total_kwh)}</p>
              </div>
              {statusBreakdown.map((s) => (
                <div
                  key={s.situation}
                  className={`p-3 rounded-md ${s.info.cls} border`}
                  data-testid={`panel-card-${s.situation}`}
                >
                  <p className="text-xs opacity-80">{s.info.emoji} {s.info.label}</p>
                  <p className="text-2xl font-bold">{s.count}</p>
                  <p className="text-xs opacity-80 mt-1">{formatKwh(s.kwh)}</p>
                </div>
              ))}
            </div>

            {cooperativesData.cooperatives.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhum cooperado encontrado para o filtro aplicado.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table data-testid="panel-linked-cooperatives-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Distribuidora</TableHead>
                        <TableHead className="text-right">kWh</TableHead>
                        <TableHead>Data Assinatura</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Parceiro Direto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedCooperatives.map(c => {
                        const st = situationInfo(c.situation);
                        return (
                          <TableRow key={c.id} data-testid={`panel-linked-coop-row-${c.id}`}>
                            <TableCell className="font-medium">{c.name || "-"}</TableCell>
                            <TableCell>{c.distributor}</TableCell>
                            <TableCell className="text-right">{formatKwh(c.contracted_kwh)}</TableCell>
                            <TableCell>{formatDate(c.signature_date)}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${st.cls}`}>
                                {st.emoji} {st.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{c.partner_name || "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination controls */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t" data-testid="panel-pagination">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Itens por página:</span>
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}
                    >
                      <SelectTrigger className="w-20" data-testid="panel-items-per-page-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-gray-500">
                      Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, cooperativesData.cooperatives.length)} - {Math.min(currentPage * itemsPerPage, cooperativesData.cooperatives.length)} de {cooperativesData.cooperatives.length} cooperados
                    </span>
                  </div>
                  {cooperativesData.cooperatives.length > itemsPerPage && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        data-testid="panel-page-prev"
                      >
                        Anterior
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                          .map((page, idx, arr) => {
                            const prev = arr[idx - 1];
                            const showEllipsis = prev && page - prev > 1;
                            return (
                              <div key={page} className="flex items-center">
                                {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                                <Button
                                  onClick={() => setCurrentPage(page)}
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  className={currentPage === page ? "bg-blue-600" : ""}
                                  data-testid={`panel-page-${page}`}
                                >
                                  {page}
                                </Button>
                              </div>
                            );
                          })}
                      </div>
                      <Button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage >= totalPages}
                        variant="outline"
                        size="sm"
                        data-testid="panel-page-next"
                      >
                        Próximo
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {showHierarchy && !loadingSubtree && subtree && (
        <>
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Network className="w-5 h-5 text-blue-600" />
                Hierarquia de <span className="text-blue-700">{subtree.root_name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-3 bg-blue-50 rounded-md">
                  <p className="text-xs text-gray-600">Parceiros (inclui raiz)</p>
                  <p className="text-2xl font-bold text-blue-700">{subtree.count}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-md">
                  <p className="text-xs text-gray-600">Cooperados Ativos</p>
                  <p className="text-2xl font-bold text-emerald-700">{totals?.cooperatives || 0}</p>
                </div>
                <div className="p-3 bg-cyan-50 rounded-md">
                  <p className="text-xs text-gray-600">kWh Contratado</p>
                  <p className="text-lg font-bold text-cyan-700">{formatKwh(totals?.kwh)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-md">
                  <p className="text-xs text-gray-600">Comissão Recebida</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(totals?.received)}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-md">
                  <p className="text-xs text-gray-600">Comissão a Receber</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(totals?.to_receive)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Parceiros na hierarquia ({subtree.count})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subtree.partners.length === 0 ? (
                <p className="text-center text-gray-500 py-6">Nenhum parceiro encontrado.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Hierarquia</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Nível</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-center">Cooperados</TableHead>
                        <TableHead className="text-right">kWh</TableHead>
                        <TableHead className="text-right">Comissão Recebida</TableHead>
                        <TableHead className="text-right">Comissão a Receber</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subtree.partners.map((p) => {
                        const indent = (p.relative_level || 0) * 16;
                        const isRoot = p.id === subtree.root_id;
                        return (
                          <TableRow key={p.id} className={isRoot ? "bg-blue-50/50" : ""}>
                            <TableCell>
                              <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
                                {p.relative_level > 0 && <ChevronsRight className="w-3 h-3 text-gray-400 mr-1" />}
                                <span className="text-xs text-gray-500">
                                  {isRoot ? "Raiz" : `Nível +${p.relative_level}`}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{p.name}</TableCell>
                            <TableCell className="text-sm">{p.email || "-"}</TableCell>
                            <TableCell className="text-sm">{p.phone || "-"}</TableCell>
                            <TableCell><Badge variant="outline">{p.level ?? "-"}</Badge></TableCell>
                            <TableCell>
                              <Badge className={p.status === "active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                                {p.status === "active" ? "Ativo" : "Pendente"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center font-medium">{p.cooperatives_count}</TableCell>
                            <TableCell className="text-right">{formatKwh(p.total_kwh)}</TableCell>
                            <TableCell className="text-right text-green-700">{formatCurrency(p.commission_received)}</TableCell>
                            <TableCell className="text-right text-amber-700">{formatCurrency(p.commission_to_receive)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Evolução Mensal de kWh Contratado
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!subtree.kwh_evolution || subtree.kwh_evolution.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sem dados de assinatura para o período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={360}>
                  <ComposedChart data={subtree.kwh_evolution} margin={{ top: 20, right: 30, left: 10, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tickFormatter={formatMonth} angle={-45} textAnchor="end" height={70} />
                    <YAxis yAxisId="left" tickFormatter={(v) => v.toLocaleString("pt-BR")} label={{ value: "kWh do mês", angle: -90, position: "insideLeft" }} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v.toLocaleString("pt-BR")} label={{ value: "kWh acumulado", angle: 90, position: "insideRight" }} />
                    <Tooltip
                      formatter={(value, name) => [Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + " kWh", name]}
                      labelFormatter={formatMonth}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="kwh" fill="#3b82f6" name="kWh contratado no mês" />
                    <Line yAxisId="right" type="monotone" dataKey="cumulative_kwh" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} name="kWh acumulado" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
